import React, { useState, useEffect } from "react";
import {
  Layout,
  Table,
  Button,
  Radio,
  Space,
  Typography,
  Tag,
  Divider,
  Alert,
  Card,
  Row,
  Col,
  Empty,
} from "antd";
import {
  SwapOutlined,
  ArrowLeftOutlined,
  CheckCircleOutlined,
} from "@ant-design/icons";
import { useLocation, useNavigate } from "react-router-dom";
import {
  GET_TRANSFER_STUDENTS,
  GET_TRANSFER_AVAILABLE_TARGETS,
  POST_TRANSFER_USERS,
  POST_TRANSFER_ALL_USERS,
} from "../../Utlis/apiPath";
import { getApi, postApi } from "../../Utlis/apiService";
import { errorToast, successToast } from "../../Services/ToastHelper";
import Loader from "../../Components/Loader/Loader";
import "./TransferPage.scss";

const { Title, Text, Paragraph } = Typography;
const { Content } = Layout;

const TransferPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const sourceClass = location.state?.sourceClass;

  const [step, setStep] = useState(1); // 1 = students, 2 = target classes, 3 = success
  const [isLoading, setIsLoading] = useState(false);
  const [students, setStudents] = useState([]);
  const [selectedBookingIds, setSelectedBookingIds] = useState([]);
  const [availableTargets, setAvailableTargets] = useState({
    standardClasses: [],
    daylightClasses: [],
  });
  const [selectedTargetClassId, setSelectedTargetClassId] = useState(null);
  const [totalStudents, setTotalStudents] = useState(0);
  const [studentsLoaded, setStudentsLoaded] = useState(false);
  const [transferredCount, setTransferredCount] = useState(0);
  const [selectedTargetClass, setSelectedTargetClass] = useState(null);

  // Determine if source class is standard or daylight
  const getSourceClassType = () => {
    if (!sourceClass) return null;
    // Check if it's a daylight class
    if (sourceClass.scheduleMode === "daylight" || sourceClass.classType === "daylight") {
      return "daylight";
    }
    return "standard";
  };

  // Get only the opposite class types to display
  const getDisplayTargets = () => {
    const sourceType = getSourceClassType();
    if (sourceType === "daylight") {
      // If source is daylight, show only standard classes
      return {
        display: availableTargets.standardClasses,
        displayType: "standard",
      };
    } else {
      // If source is standard, show only daylight classes
      return {
        display: availableTargets.daylightClasses,
        displayType: "daylight",
      };
    }
  };

  // Redirect to ClassManagement if no source class
  useEffect(() => {
    if (!sourceClass?.id) {
      navigate("/standard-class");
    }
  }, [sourceClass, navigate]);

  const fetchStudents = async () => {
    if (!sourceClass?.id) return;
    setIsLoading(true);
    try {
      const { status, data } = await getApi(GET_TRANSFER_STUDENTS, {
        params: { classId: sourceClass.id },
      });
      if (status === 200) {
        setStudents(data.bookings || []);
        setTotalStudents(data.totalStudents || 0);
        setStudentsLoaded(true);
      } else {
        setStudents([]);
      }
    } catch (error) {
      console.error("Error fetching students:", error);
      errorToast("Failed to load students.");
      setStudents([]);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchAvailableTargets = async () => {
    if (!sourceClass?.id) return;
    setIsLoading(true);
    try {
      const { status, data } = await getApi(GET_TRANSFER_AVAILABLE_TARGETS, {
        params: { sourceClassId: sourceClass.id },
      });
      if (status === 200) {
        setAvailableTargets({
          standardClasses: data.standardClasses || [],
          daylightClasses: data.daylightClasses || [],
        });
        setStep(2);
      } else {
        errorToast("Failed to load available target classes.");
      }
    } catch (error) {
      console.error("Error fetching available targets:", error);
      errorToast("Failed to load available target classes.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleTransferConfirm = async () => {
    if (!selectedTargetClassId) {
      errorToast("Please select a target class.");
      return;
    }

    const isAllSelected = selectedBookingIds.length === students.length;
    setIsLoading(true);
    try {
      let status, message;

      if (isAllSelected) {
        ({ status, message } = await postApi(POST_TRANSFER_ALL_USERS, {
          sourceClassId: sourceClass.id,
          targetClassId: selectedTargetClassId,
        }));
      } else {
        ({ status, message } = await postApi(POST_TRANSFER_USERS, {
          sourceClassId: sourceClass.id,
          targetClassId: selectedTargetClassId,
          bookingIds: selectedBookingIds,
        }));
      }

      if (status === 200) {
        setTransferredCount(
          isAllSelected ? students.length : selectedBookingIds.length
        );
        setStep(3);
        successToast(message || "Students transferred successfully.");
      } else {
        errorToast(message || "Transfer failed.");
      }
    } catch (error) {
      console.error("Error transferring students:", error);
      errorToast("An error occurred during transfer.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleBack = () => {
    if (step === 2) {
      setStep(1);
      setSelectedTargetClassId(null);
    } else {
      navigate("/standard-class");
    }
  };

  const handleClose = () => {
    navigate("/standard-class");
  };

  const rowSelection = {
    selectedRowKeys: selectedBookingIds,
    onChange: (selectedKeys) => {
      setSelectedBookingIds(selectedKeys);
    },
  };

  const studentColumns = [
    {
      title: "S.No",
      key: "slNo",
      render: (_, __, index) => index + 1,
      width: 60,
    },
    {
      title: "Name",
      key: "name",
      render: (_, record) =>
        `${record.customerId?.firstName || ""} ${record.customerId?.lastName || ""}`.trim() ||
        "N/A",
    },
    {
      title: "Email",
      key: "email",
      render: (_, record) => record.customerId?.email || "N/A",
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status) => (
        <Tag color={status === "completed" ? "green" : "orange"}>
          {status?.charAt(0).toUpperCase() + status?.slice(1)}
        </Tag>
      ),
    },
    {
      title: "Booking Date",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (date) => (date ? new Date(date).toLocaleDateString() : "N/A"),
    },
  ];

  const TargetClassOption = ({ cls }) => (
    <Card
      hoverable
      style={{
        border: `2px solid ${selectedTargetClassId === cls.id ? "#1890ff" : "#e8e8e8"}`,
        background: selectedTargetClassId === cls.id ? "#e6f7ff" : "#fff",
        marginBottom: 12,
        cursor: "pointer",
        transition: "all 0.3s",
      }}
      onClick={() => {
        setSelectedTargetClassId(cls.id);
        setSelectedTargetClass(cls);
      }}
    >
      <Row gutter={16} align="middle">
        <Col span={1}>
          <Radio checked={selectedTargetClassId === cls.id} />
        </Col>
        <Col span={23}>
          <div>
            <Text strong style={{ fontSize: 15 }}>
              {cls.title}
            </Text>
            <div style={{ marginTop: 8, fontSize: 13, color: "#666" }}>
              <div
                style={{
                  marginBottom: 8,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  display: "-webkit-box",
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: "vertical",
                }}
                dangerouslySetInnerHTML={{ __html: cls.description }}
              />
              <div>
                <Tag color="blue">{cls.joinType}</Tag>
                <Tag color="green">{cls.bookingType}</Tag>
                <Text type="secondary">
                  Instructor: {cls.instructorName} | Price: {cls.currency}{" "}
                  {cls.price}
                </Text>
              </div>
            </div>
          </div>
        </Col>
      </Row>
    </Card>
  );

  // Load students on component mount
  useEffect(() => {
    if (step === 1 && !studentsLoaded) {
      fetchStudents();
    }
  }, [step, studentsLoaded]);

  if (!sourceClass) {
    return null;
  }

  if (isLoading && step === 1 && students.length === 0) {
    return <Loader />;
  }

  return (
    <div className="transfer-page">
      {isLoading && <Loader />}

      <Content style={{ padding: "24px" }}>
        {/* Header */}
        <div className="transfer-header">
          <Space size="large" style={{ marginBottom: 24 }}>
            <Button
              type="text"
              icon={<ArrowLeftOutlined />}
              onClick={handleBack}
              size="large"
            >
              Back
            </Button>
            <div>
              <Title level={2} style={{ margin: 0 }}>
                <SwapOutlined /> Transfer Students
              </Title>
              <Text type="secondary" style={{ fontSize: 14 }}>
                From: {sourceClass?.title}
              </Text>
            </div>
          </Space>
        </div>

        {/* Step 1: Select Students */}
        {step === 1 && (
          <Card>
            <div className="transfer-step">
              <Title level={4}>Step 1: Select Students to Transfer</Title>
              <Divider />

              {totalStudents > 0 && (
                <Alert
                  message={`${totalStudents} active student(s) found in "${sourceClass?.title}"`}
                  type="info"
                  showIcon
                  style={{ marginBottom: 16 }}
                />
              )}

              {students.length > 0 ? (
                <>
                  <div style={{ overflowX: "auto", marginBottom: 16 }}>
                    <Table
                      rowKey="id"
                      columns={studentColumns}
                      dataSource={students}
                      rowSelection={rowSelection}
                      loading={isLoading}
                      pagination={{
                        pageSize: 10,
                        showSizeChanger: true,
                        showTotal: (total) => `Total ${total} students`,
                      }}
                      scroll={{ x: 600 }}
                    />
                  </div>

                  <div
                    style={{
                      padding: 12,
                      background: "#f5f5f5",
                      borderRadius: 4,
                      marginBottom: 16,
                    }}
                  >
                    {selectedBookingIds.length === students.length
                      ? "✓ All students selected — will use bulk transfer."
                      : selectedBookingIds.length > 0
                      ? `✓ ${selectedBookingIds.length} student(s) selected — will transfer individually.`
                      : "Select at least one student to proceed."}
                  </div>

                  <div style={{ textAlign: "right" }}>
                    <Button
                      type="primary"
                      icon={<SwapOutlined />}
                      size="large"
                      disabled={selectedBookingIds.length === 0}
                      onClick={fetchAvailableTargets}
                      loading={isLoading}
                    >
                      Next: Select Target Class ({selectedBookingIds.length})
                    </Button>
                  </div>
                </>
              ) : (
                <Empty
                  description="No active students found"
                  style={{ marginTop: 40 }}
                />
              )}
            </div>
          </Card>
        )}

        {/* Step 2: Select Target Class */}
        {step === 2 && (
          <Card>
            <div className="transfer-step">
              <Title level={4}>Step 2: Select Target Class</Title>
              <Divider />

              <Alert
                message={`Transferring ${
                  selectedBookingIds.length === students.length
                    ? "all"
                    : selectedBookingIds.length
                } student(s). Select a target ${getDisplayTargets().displayType} class below.`}
                type="info"
                showIcon
                style={{ marginBottom: 24 }}
              />

              {getDisplayTargets().display.length > 0 ? (
                <>
                  <Title level={5} style={{ marginBottom: 12, marginTop: 24 }}>
                    {getDisplayTargets().displayType === "daylight"
                      ? "Daylight Classes"
                      : "Standard Classes"}
                  </Title>
                  <div
                    style={{
                      marginBottom: 24,
                      paddingLeft: 12,
                      borderLeft: `3px solid ${
                        getDisplayTargets().displayType === "daylight"
                          ? "#faad14"
                          : "#1890ff"
                      }`,
                    }}
                  >
                    {getDisplayTargets().display.map((cls) => (
                      <TargetClassOption key={cls.id} cls={cls} />
                    ))}
                  </div>
                </>
              ) : (
                <Alert
                  message={`No available ${getDisplayTargets().displayType} target classes found.`}
                  type="warning"
                  showIcon
                  style={{ marginTop: 24 }}
                />
              )}

              {selectedTargetClass && (
                <Card
                  style={{
                    background: "#f0f5ff",
                    marginTop: 24,
                    border: "1px solid #1890ff",
                  }}
                >
                  <Row gutter={16}>
                    <Col span={2}>
                      <CheckCircleOutlined
                        style={{ fontSize: 20, color: "#1890ff" }}
                      />
                    </Col>
                    <Col span={22}>
                      <div>
                        <Text strong>
                          Selected: {selectedTargetClass.title}
                        </Text>
                        <Paragraph style={{ marginTop: 8, marginBottom: 0 }}>
                          Instructor: {selectedTargetClass.instructorName} |
                          Price: {selectedTargetClass.currency}{" "}
                          {selectedTargetClass.price}
                        </Paragraph>
                      </div>
                    </Col>
                  </Row>
                </Card>
              )}

              <div style={{ textAlign: "right", marginTop: 24 }}>
                <Space>
                  <Button size="large" onClick={handleBack}>
                    Back
                  </Button>
                  <Button
                    type="primary"
                    size="large"
                    disabled={!selectedTargetClassId}
                    onClick={handleTransferConfirm}
                    loading={isLoading}
                  >
                    Confirm Transfer
                  </Button>
                </Space>
              </div>
            </div>
          </Card>
        )}

        {/* Step 3: Success */}
        {step === 3 && (
          <Card>
            <div
              className="transfer-step"
              style={{ textAlign: "center", padding: "40px 20px" }}
            >
              <CheckCircleOutlined
                style={{ fontSize: 60, color: "#52c41a", marginBottom: 16 }}
              />
              <Title level={2} style={{ color: "#52c41a" }}>
                Transfer Successful!
              </Title>
              <Paragraph style={{ fontSize: 16, marginBottom: 24 }}>
                {transferredCount} student(s) have been successfully transferred
                from <Text strong>{sourceClass?.title}</Text> to{" "}
                <Text strong>{selectedTargetClass?.title}</Text>
              </Paragraph>

              <Divider />

              <div style={{ marginTop: 32 }}>
                <Button
                  type="primary"
                  size="large"
                  onClick={handleClose}
                  style={{ minWidth: 180 }}
                >
                  Back to Classes
                </Button>
              </div>
            </div>
          </Card>
        )}
      </Content>
    </div>
  );
};

export default TransferPage;
